import { DateFormatter } from '@repo/toolkit/date'
import { SVGFC, Version } from '@repo/toolkit/types'
import ActionButton from '@repo/ui/action-button'
import Badge from '@repo/ui/badge'
import Button, { ButtonVariant } from '@repo/ui/button'
import { Column, ExpandedColumn } from '@repo/ui/data-table'
import {
  LockClosedIcon,
  PenIcon,
  PlusIcon,
  UserCheckIcon,
  UserIcon,
  UserRemoveIcon
} from '@repo/ui/icons'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import { useMutation } from '@tanstack/react-query'
import React, { useState } from 'react'
import { toast } from 'sonner'

import { API } from '../../api/apis.config'
import { impersonateUser } from '../../api/auth/impersonate-user'
import { createUser } from '../../api/user/create-user'
import { updateUser } from '../../api/user/update-user'
import { updateUserStatus } from '../../api/user/update-user-status'
import FilterDrawer from '../../components/elements/drawers/filter-drawer'
import DynamicDataTable, { DynamicTableRef } from '../../components/elements/dynamic-table'
import { countFilters } from '../../components/elements/dynamic-table/utils/countFilters'
import UserNavBar from '../../components/elements/nav-bar'
import { getUserDefaultRedirectionPath } from '../../components/pages/login/utils'
import SystemAdminUserForm from '../../components/pages/system-admin/system-admin-user-form'
import useAuth from '../../lib/hooks/use-auth'
import { useFilters } from '../../lib/hooks/use-filters'
import { useModalManager } from '../../lib/hooks/use-modal-manager'
import { currencyEnumMapping } from '../../lib/types/enums/common/currency.enum'
import { olTypeEnumMapping } from '../../lib/types/enums/ol/ol-type.enum'
import { staffPositionEnumMapping } from '../../lib/types/enums/staff/staff-position.enum'
import { RoleEnum, roleEnumMapping } from '../../lib/types/enums/users/role.enum'
import { UserStatusEnum, userStatusMapping } from '../../lib/types/enums/users/user-status.enum'
import {
  SystemAdminUser,
  SystemAdminUserInput,
  UserFilters,
  UserSession
} from '../../lib/types/models/users/user'
import { modalButtonConfig } from '../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../lib/utils/functions/misc.functions'
import { isUserFirstNameOptional } from '../../lib/utils/functions/user.functions'
import { t } from '../../locale/utils'
import SystemAdminUserFilter from './system-admin-user-filter'

const initialFilters: UserFilters = {}
type SystemAdminUserVersion = Version | 'SYSTEM_ADMIN'

type StatusAction = {
  status: UserStatusEnum
  label: string
  variant: ButtonVariant
  icon: SVGFC
  confirmationTitle: (user: SystemAdminUser) => string
  successMessage: string
}

type StatusChange = { user: SystemAdminUser; action: StatusAction }

const activateAction: StatusAction = {
  status: UserStatusEnum.ACTIVE,
  label: 'Activate',
  variant: 'success',
  icon: UserCheckIcon,
  confirmationTitle: ({ firstName, lastName }) =>
    `Are you sure you want to activate the account of ${firstName} ${lastName}?`,
  successMessage: 'Account activated'
}

// An active account can be deactivated or blocked, any other one can only be activated back
const statusActionsByStatus: Record<UserStatusEnum, StatusAction[]> = {
  [UserStatusEnum.ACTIVE]: [
    {
      status: UserStatusEnum.INACTIVE,
      label: 'Deactivate',
      variant: 'warning',
      icon: UserRemoveIcon,
      confirmationTitle: ({ firstName, lastName }) =>
        `Are you sure you want to deactivate the account of ${firstName} ${lastName}? Their active sessions will be closed.`,
      successMessage: 'Account deactivated'
    },
    {
      status: UserStatusEnum.BLOCKED,
      label: 'Block',
      variant: 'destructive',
      icon: LockClosedIcon,
      confirmationTitle: ({ firstName, lastName }) =>
        `Are you sure you want to block the account of ${firstName} ${lastName}? Their active sessions will be closed.`,
      successMessage: 'Account blocked'
    }
  ],
  [UserStatusEnum.INACTIVE]: [activateAction],
  [UserStatusEnum.BLOCKED]: [activateAction]
}

const SystemAdminUsersListPage = () => {
  const { user } = useAuth()
  const [isTableLoading, setIsTableLoading] = useState(false)
  const [impersonatedUserId, setImpersonatedUserId] = useState<number>()
  const tableRef = React.useRef<DynamicTableRef<SystemAdminUser>>(null)
  const { selectedFilter, filter, onFilterChange, resetFilters, onSelectedFilterChange } =
    useFilters<UserFilters>({
      initialFilters
    })
  const [pendingStatusChange, setPendingStatusChange] = useState<StatusChange>()
  const { modalState, selectedData, toggleModal, closeModel } = useModalManager<
    'filters' | 'status' | 'create' | 'edit',
    SystemAdminUser
  >(['filters', 'status', 'create', 'edit'])

  const impersonateUserMutation = useMutation({
    mutationFn: impersonateUser,
    onError: showAxiosError,
    onSuccess: (data) => {
      // A full page load: nothing of the previous session keeps running
      window.location.assign(getUserDefaultRedirectionPath(data))
    }
  })

  const updateUserStatusMutation = useMutation({
    mutationFn: ({ user, action }: StatusChange) =>
      updateUserStatus({ id: user.id, status: action.status }),
    onError: showAxiosError,
    onSuccess: (_, { action }) => {
      toast.success(action.successMessage)
      closeModel('status')
      setPendingStatusChange(undefined)
      tableRef.current?.refetch()
    }
  })

  const createUserMutation = useMutation({
    mutationFn: createUser,
    onError: showAxiosError,
    onSuccess: () => {
      toast.success('User created, an email was sent to set the password')
      closeModel('create')
      tableRef.current?.refetch()
    }
  })

  const updateUserMutation = useMutation({
    mutationFn: updateUser,
    onError: showAxiosError,
    onSuccess: () => {
      toast.success('User updated')
      closeModel('edit')
      tableRef.current?.refetch()
    }
  })

  const onEditSubmit = (values: SystemAdminUserInput) => {
    if (!selectedData) return
    updateUserMutation.mutate({ id: selectedData.id, ...values })
  }

  const columns: Column<SystemAdminUser>[] = [
    {
      accessorKey: 'firstName',
      header: 'Prénom',
      enableSorting: true
    },
    {
      accessorKey: 'lastName',
      header: 'Nom',
      enableSorting: true
    },
    {
      accessorKey: 'email',
      header: 'Email'
    },
    {
      accessorKey: 'role',
      header: 'Rôle',
      cell: ({ row }) => (
        <div className="flex flex-row flex-nowrap items-center gap-2">
          <Badge text={roleEnumMapping[row.original.role]} variant="white" />
          {row.original.role === RoleEnum.STAFF && row.original.staff?.position && (
            <Badge
              text={t(staffPositionEnumMapping[row.original.staff.position])}
              variant="grey"
            />
          )}
          {row.original.role === RoleEnum.Ol && row.original.ol?.type && (
            <Badge
              text={olTypeEnumMapping[row.original.ol.type].label}
              variant={olTypeEnumMapping[row.original.ol.type].variant}
            />
          )}
          {row.original.role === RoleEnum.Ol && row.original.ol?.currency && (
            <Badge text={currencyEnumMapping[row.original.ol.currency].label} variant="grey" />
          )}
        </div>
      )
    },
    {
      accessorKey: 'status',
      header: 'Statut',
      cell: ({ row }) => (
        <div className="flex flex-row flex-nowrap items-center gap-2">
          <Badge text={userStatusMapping[row.original.status]} variant="grey" />
          {/* A system admin cannot change the status of their own account */}
          {row.original.id !== user.id &&
            statusActionsByStatus[row.original.status].map((action) => (
              <Button
                key={action.status}
                variant={action.variant}
                icon={action.icon}
                label={action.label}
                loading={
                  updateUserStatusMutation.isPending &&
                  pendingStatusChange?.user.id === row.original.id &&
                  pendingStatusChange.action.status === action.status
                }
                onClick={(event) => {
                  event.stopPropagation()
                  setPendingStatusChange({ user: row.original, action })
                  toggleModal('status')
                }}
              />
            ))}
        </div>
      )
    },
    {
      accessorKey: 'isMasterLec',
      header: 'Master LEC',
      cell: ({ row }) => <Badge text={row.original.isMasterLec ? 'Oui' : 'Non'} variant="white" />
    },
    {
      accessorKey: 'userGroup',
      header: 'Groupe',
      cell: ({ row }) => row.original.userGroup?.name ?? '-'
    },
    {
      accessorKey: 'id',
      header: 'Impersonation',
      cell: ({ row }) => (
        <Button
          variant="primary"
          label="Impersonate"
          disabled={row.original.id === user.id || row.original.status !== UserStatusEnum.ACTIVE}
          loading={impersonateUserMutation.isPending && impersonatedUserId === row.original.id}
          onClick={(event) => {
            event.stopPropagation()
            setImpersonatedUserId(row.original.id)
            impersonateUserMutation.mutate(row.original.id)
          }}
        />
      )
    },
    {
      // The Impersonation column already uses "id", so the edit column is keyed on another
      // field that is not displayed on its own
      accessorKey: 'phoneNumber',
      header: '',
      cell: ({ row }) => (
        <ActionButton
          variant="grey"
          icon={PenIcon}
          onClick={(event) => {
            event.stopPropagation()
            toggleModal('edit', row.original)
          }}
        />
      )
    }
  ]

  // Each expanded column is keyed on a parent column so the session lines up under it
  const sessionColumns: ExpandedColumn<SystemAdminUser, UserSession>[] = [
    {
      accessorKey: 'firstName',
      header: 'Session',
      display: (session) =>
        session?.authenticatedAt ? DateFormatter.ddMMYYYY_HHMM(session.authenticatedAt) : '-'
    },
    {
      accessorKey: 'email',
      header: 'IP',
      display: (session) => session?.ip ?? '-'
    },
    {
      accessorKey: 'role',
      header: 'Type',
      display: (session) => (
        <Badge
          text={session?.isImpersonation ? 'Impersonation' : 'Direct'}
          variant={session?.isImpersonation ? 'grey' : 'white'}
        />
      )
    },
    {
      accessorKey: 'status',
      header: 'Appareil',
      display: (session) => (
        <span title={session?.userAgent} className="block max-w-xs truncate">
          {session?.userAgent ?? '-'}
        </span>
      )
    },
    {
      accessorKey: 'userGroup',
      header: 'Expire le',
      display: (session) =>
        session?.expiresAt ? DateFormatter.ddMMYYYY_HHMM(session.expiresAt) : '-'
    }
  ]

  return (
    <div>
      <UserNavBar title="Utilisateurs System Admin" icon={UserIcon} />
      <div className="h-4" />

      <DynamicDataTable<SystemAdminUser, UserFilters, UserSession, SystemAdminUserVersion>
        ref={tableRef}
        onLoadingChange={setIsTableLoading}
        title="Tous les utilisateurs"
        columns={columns}
        expandedOptions={{ key: 'sessions', columns: sessionColumns }}
        endpoint={API.user.getAll.url}
        version="SYSTEM_ADMIN"
        filters={selectedFilter}
        filtersLength={countFilters(selectedFilter)}
        onFiltersClick={() => toggleModal('filters')}
        permissions={{ delete: false }}
        actions={[
          {
            type: 'global',
            icon: PlusIcon,
            label: 'Create admin',
            fn: () => toggleModal('create')
          }
        ]}
      />

      <FilterDrawer
        isOpen={modalState.filters}
        selectedFilter={selectedFilter}
        isLoading={isTableLoading}
        onClose={() => closeModel('filters')}
        onReset={resetFilters}
        onApply={() => onSelectedFilterChange({ ...filter })}
        onFilterChange={onFilterChange}
      >
        <SystemAdminUserFilter filter={filter} onFilterChange={onFilterChange} />
      </FilterDrawer>

      {/* Create */}
      <Modal
        icon={UserIcon}
        isOpen={modalState.create}
        title="Create an admin"
        hideActions
        onCancel={() => closeModel('create')}
      >
        <SystemAdminUserForm
          className={MODAL_BODY_CLASSNAME}
          buttonsConfig={modalButtonConfig<SystemAdminUserInput>({
            isEnglish: true,
            confirmText: 'Create',
            isLoading: createUserMutation.isPending,
            onCancel: () => closeModel('create'),
            onConfirm: (values) => createUserMutation.mutate(values)
          })}
        />
      </Modal>

      {/* Edit */}
      {selectedData && (
        <Modal
          icon={UserIcon}
          isOpen={modalState.edit}
          title="Edit the user"
          hideActions
          onCancel={() => closeModel('edit')}
        >
          <SystemAdminUserForm
            className={MODAL_BODY_CLASSNAME}
            isFirstNameOptional={isUserFirstNameOptional(selectedData)}
            defaultValues={{
              firstName: selectedData.firstName,
              lastName: selectedData.lastName,
              email: selectedData.email,
              phoneNumber: selectedData.phoneNumber
            }}
            buttonsConfig={modalButtonConfig<SystemAdminUserInput>({
              isEnglish: true,
              confirmText: 'Save',
              isLoading: updateUserMutation.isPending,
              onCancel: () => closeModel('edit'),
              onConfirm: onEditSubmit
            })}
          />
        </Modal>
      )}

      {pendingStatusChange && (
        <Modal
          isOpen={modalState.status}
          icon={pendingStatusChange.action.icon}
          isLoading={updateUserStatusMutation.isPending}
          variant={
            pendingStatusChange.action.status === UserStatusEnum.ACTIVE
              ? 'confirmation'
              : 'destructive'
          }
          title={pendingStatusChange.action.confirmationTitle(pendingStatusChange.user)}
          onCancel={() => {
            closeModel('status')
            setPendingStatusChange(undefined)
          }}
          actionButtonsOptions={{
            cancelText: 'Cancel',
            confirmText: pendingStatusChange.action.label,
            onConfirm: () => updateUserStatusMutation.mutate(pendingStatusChange)
          }}
        />
      )}
    </div>
  )
}

export default SystemAdminUsersListPage
